<template>
  <div class="w-100 grid-container">
    <vt-side-panel :visible="showSidePanel">
      <configuration-sidepanel
        :show-operation-tooltip="false"
        :show-operation-group-filter="true"
        :association-type="ASSOCIATION_TYPE.OPERATION.key"
        :edit-disabled="clickedRow ? clickedRow.isSystemRole : null"
        :sidepanel-title="sidepanelTitle"
        :association="$tc('configuration.modal.operation', selectedRoleOperations.length)"
        :icon="'far fa-pencil-ruler'"
        :list="selectedRoleOperations"
        @closeConfigurationSidepanel="showSidePanel = false"
        @update="showRoleModal($t('configuration.actions.update'), clickedRow, $event); showSidePanel = false"
      />
    </vt-side-panel>
    <configuration-section-header
      class="my-0"
      :section-icon="'far fa-pencil-ruler'"
      :section-title="$tc('configuration.sections.roles', 2)"
      :section-subtitle="msgSelectedRows"
      @changedPageSize="changedPageSize($event)"
      @changedMode="changedMode($event)"
    >
      <b-button
        v-shortkey="['alt', 'del']"
        v-tooltip="$t('configuration.actions.delete')"
        :disabled="!!(selectedRows.find(role => role.isSystemRole)) || selectedRows.length < 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="deleteRoles(selectedRows)"
        @shortkey="deleteRoles(selectedRows)"
      >
        <i
          class="far fa-trash-alt"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['alt', 'e']"
        v-tooltip="$t('configuration.actions.update')"
        :disabled="!!(selectedRows.find(role => role.isSystemRole)) || selectedRows.length !== 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showRoleModal($t('configuration.actions.update'), selectedRows[0])"
        @shortkey="showRoleModal($t('configuration.actions.update'), selectedRows[0])"
      >
        <i
          class="far fa-edit"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['ctrl', 'enter']"
        v-tooltip="$t('configuration.actions.add')"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showRoleModal($t('configuration.actions.add'))"
        @shortkey="showRoleModal($t('configuration.actions.add'))"
      >
        <i
          class="far fa-plus"
          aria-hidden="true"
        />
      </b-button>
    </configuration-section-header>
    <div class="flex-grow-1">
      <vt-grid
        :autosize-columns="false"
        class="configuration-grid people-roles-grid"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-code="gridUiComponent"
      />
    </div>
    <modal-wrapper ref="roleModalWrapper">
      <role-modal
        ref="roleModal"
        :action-title="actionTitle"
        @update="patchRole($event)"
        @create="createRole($event)"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import columnsRolesGrid from '@/modules/configurationModule/detail/peopleManagement/roles/columnsRolesGrid'
import RoleModal from '@/modules/configurationModule/detail/peopleManagement/roles/RoleModal'
import ConfigurationSidepanel from '@/modules/configurationModule/detail/ConfigurationSidepanel'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import { PaginatorDatasource } from '@/shared/resources/datasources/paginator/paginator-datasource'
import RoleConfigurationServices from '@/api/resources/configuration/peopleManagement/role/RoleConfigurationServices'
import RoleOperationConfigurationServices from '@/api/resources/configuration/peopleManagement/role/RoleOperationConfigurationServices'
import { ASSOCIATION_TYPE } from '@/api/resources/configuration/peopleManagement/AssociationTypes'

export default {
    name: 'PeopleRolesGrid',
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        RoleModal,
        ConfigurationSidepanel
    },
    data() {
        return {
            gridUiComponent: 'uicomponent.configuration.global.peopleManagement.roles',
            showSidePanel: false,
            sidepanelTitle: null,
            actionTitle: null,
            selectedRows: [],
            columnDefs: columnsRolesGrid(),
            selectedRoleOperations: [],
            clickedRow: null,
            mode: {
                size: 25
            }
        }
    },
    computed: {
        msgSelectedRows() {
            let msg = `${this.selectedRows.length} ${this.$tc('global.selectedElements', this.selectedRows.length)}`
            if (this.selectedRows.length === 0) msg = ''
            return msg
        },
        rowHeight() {
            return this.mode?.size
        },
        ASSOCIATION_TYPE() {
            return ASSOCIATION_TYPE
        },
        selectedRowsHasSystemRole() {
            return !!(this.selectedRows.find(role => role.isSystemRole))
        },
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                onRowClicked: this.onRoleRowClicked,
                getContextMenuItems: this.getContextMenuItems,
                getRowHeight: () => this.rowHeight,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged
            }
        }
    },
    methods: {
        getContextMenuItems(params) {
            if (params?.node && !this.selectedRowsHasSystemRole) {
                return [
                    {
                        name: this.$tc('configuration.actions.update'),
                        action: () => this.showRoleModal(this.$tc('configuration.actions.update'), params?.node.data)
                    },
                    {
                        name: this.selectedRows.length ? this.$tc('configuration.actions.deleteSelection') : this.$tc('configuration.actions.delete'),
                        action: () => {
                            const rolesToDelete = this.selectedRows.length ? this.selectedRows : [params?.node.data]
                            this.deleteRoles(rolesToDelete)
                        }
                    }
                ]
            }
            return [
            ]
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            this.reloadData()
        },
        async onRoleRowClicked(params) {
            this.updateLoading(true)
            const [err, response] = await this.toCall(RoleOperationConfigurationServices.getRoleOperationsByRoleId(params?.node.data.id))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.clickedRow = params?.node.data
            this.selectedRoleOperations = response
            this.showSidePanel = true
            this.sidepanelTitle = params?.node.data.name
        },
        onGridReady({ api }) {
            this.gridApi = api
            api.setServerSideDatasource(new PaginatorDatasource(RoleConfigurationServices))
        },
        showRoleModal(title, selectedRole, associationType) {
            this.actionTitle = title
            this.$refs.roleModalWrapper.show([selectedRole, associationType])
        },
        async patchRole(role) {
            this.updateLoading(true)
            const [err, data] = await this.toCall(RoleConfigurationServices.patchRole([role]))
            this.updateLoading(false)
            if (err) {
                this.$refs.roleModalWrapper.hide()
                this.showErrorModalGlobal()
                return
            } else if (data.length) {
                this.$refs.roleModalWrapper.hide()
                this.handleErrors(data)
                return
            }
            this.$refs.roleModalWrapper.hide()
            this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 1))
            this.reloadData()
        },
        async createRole(role) {
            const response = await this.$_toCallUnique(RoleConfigurationServices.createRole(role))
            if (response) {
                await this.$refs.roleModal.setRoleOperations(response)
                this.actionTitle = this.$tc('configuration.actions.update')
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
                this.reloadData()
            }
        },
        async deleteRolesBatch(roles) {
            const rolesToDelete = roles.map(role => role.id)
            this.updateLoading(true)
            const [err, response] = await this.toCall(RoleConfigurationServices.deleteRolesBatch(rolesToDelete))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            } else if (response.length) {
                this.handleErrors(response)
                return
            }
            if (rolesToDelete.length > 1) this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 2, { elements: rolesToDelete.length }))
            else this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 1))
            this.reloadData()
        },
        reloadData() {
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(new PaginatorDatasource(RoleConfigurationServices))
        },
        async deleteRoles(roles) {
            if (roles.length) {
                const promise = await this.showConfirmationModalGlobal(`${this.$t('configuration.grid.deleteConfirmation')} ${roles.length} ${this.$tc('configuration.grid.element', roles.length)}, <br/> ${this.$t('configuration.grid.deleteQuestion')}`, this.$t('configuration.actions.delete'))
                if (promise) {
                    await this.deleteRolesBatch(roles)
                }
            }
        }
    }
}
</script>
